import { useNavigate } from "react-router-dom";
import { Dumbbell, Apple, LineChart, Sparkles, Check, ArrowRight } from "lucide-react";
import Chatbot from "@/components/Chatbot";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const HERO = "https://images.unsplash.com/photo-1534258936925-c58bed479fcb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const features = [
    { icon: Dumbbell, title: "Programme sur mesure", desc: "Un plan d'entraînement 4 semaines adapté à ton niveau et ton objectif." },
    { icon: Apple, title: "Plan alimentaire", desc: "Des repas simples et équilibrés, ajustés à tes préférences." },
    { icon: LineChart, title: "Suivi motivant", desc: "Graphiques de progression et un arbre qui pousse avec toi." },
    { icon: Sparkles, title: "Bilan mensuel", desc: "Chaque mois, un nouveau programme recalculé selon tes progrès." },
  ];

  const plans = [
    { name: "Essentiel", price: "10 €", period: "/mois", feats: ["Programme 4 semaines", "Plan alimentaire", "Export PDF", "Suivi + arbre animé"] },
    { name: "Premium", price: "17 €", period: "/mois", highlight: true, feats: ["Tout Essentiel", "Coach IA illimité", "Bilans bi-mensuels", "Priorité nouveautés"] },
    { name: "Annuel", price: "170 €", period: "/an", feats: ["Tout Premium", "2 mois offerts", "Meilleur prix"] },
  ];

  return (
    <div className="min-h-screen bg-[#0A0F0D] text-white">
      <header className="sticky top-0 z-40 glass border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <Logo size="sm" />
        <button data-testid="landing-login-button" onClick={() => navigate(user ? "/app" : "/login")}
          className="text-sm font-semibold text-white/80 hover:text-white">
          {user ? "Mon espace" : "Se connecter"}
        </button>
      </header>

      <section className="relative overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0F0D]/60 via-[#0A0F0D]/80 to-[#0A0F0D]" />
        <div className="relative max-w-md mx-auto sm:max-w-2xl px-5 pt-16 pb-20 text-center">
          <span className="inline-block rounded-full border border-lime-400/40 bg-lime-400/10 px-4 py-1.5 text-xs font-semibold text-lime-300 mb-6 animate-float-up">
            Ton coach de poche, sans te ruiner
          </span>
          <h1 data-testid="landing-hero-title" className="font-display text-4xl sm:text-6xl font-black leading-[1.05] tracking-tight animate-float-up">
            Chaque séance<br /><span className="text-lime-400">te fait pousser.</span>
          </h1>
          <p className="mt-5 text-gray-300 text-base sm:text-lg animate-float-up">
            Réponds à un questionnaire, reçois un programme d'entraînement et un plan alimentaire
            personnalisés sur 4 semaines. Ajustés chaque mois.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center animate-float-up">
            <button data-testid="landing-start-questionnaire-button" onClick={() => navigate("/questionnaire")}
              className="inline-flex items-center justify-center gap-2 bg-lime-400 text-black font-extrabold px-6 py-3.5 rounded-xl hover:bg-[#b0e600] active:scale-95 transition-all shadow-[0_0_20px_rgba(200,255,0,0.3)]">
              Commencer maintenant <ArrowRight className="h-4 w-4" />
            </button>
            <button onClick={() => navigate(user ? "/app" : "/login")}
              className="inline-flex items-center justify-center border border-white/15 px-6 py-3.5 rounded-xl font-semibold hover:bg-white/5 transition-all">
              J'ai déjà un compte
            </button>
          </div>
          <p className="mt-6 text-xs text-white/40">Réservé aux 18 ans et plus · 7 jours d'essai gratuit · sans carte</p>
        </div>
      </section>

      <section className="max-w-md mx-auto sm:max-w-4xl px-5 py-16">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-center mb-10">Tout ce qu'il te faut pour avancer</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {features.map((f, i) => (
            <div key={i} className="rounded-2xl p-5 border border-white/10 bg-[#141C17] hover:border-lime-400/40 transition-all duration-300">
              <div className="h-11 w-11 rounded-xl bg-lime-400/15 flex items-center justify-center mb-4">
                <f.icon className="h-5 w-5 text-lime-400" />
              </div>
              <h3 className="font-display font-semibold text-lg mb-1.5">{f.title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-md mx-auto sm:max-w-4xl px-5 pb-20">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-center mb-2">Des formules simples</h2>
        <p className="text-center text-sm text-lime-400 font-semibold mb-10">Commence par 7 jours d'essai gratuit, sans carte 🎁</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {plans.map((p) => (
            <div key={p.name} className={`rounded-2xl p-6 border ${p.highlight ? "border-lime-400/60 bg-lime-400/[0.06]" : "border-white/10 bg-[#141C17]"}`}>
              {p.highlight && <span className="text-xs font-bold text-lime-400">RECOMMANDÉ</span>}
              <h3 className="font-display text-xl font-bold mt-1">{p.name}</h3>
              <div className="mt-2 mb-4"><span className="text-3xl font-black">{p.price}</span><span className="text-white/50 text-sm">{p.period}</span></div>
              <ul className="space-y-2">
                {p.feats.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-gray-300">
                    <Check className="h-4 w-4 text-lime-400 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <button onClick={() => navigate("/questionnaire")}
            className="inline-flex items-center gap-2 bg-lime-400 text-black font-extrabold px-8 py-3.5 rounded-xl hover:bg-[#b0e600] active:scale-95 transition-all">
            Créer mon programme <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>

      <footer className="border-t border-white/10 px-5 py-8 text-center">
        <p className="text-xs text-white/40 max-w-md mx-auto">
          Programme bien-être général, ne remplace pas l'avis d'un professionnel de santé.
        </p>
      </footer>
      <Chatbot />
    </div>
  );
}
